import { ArrowLeft, X, CalendarDays, Loader2 } from "lucide-react";
import { createContext, useContext, useEffect, useMemo, useRef } from "react";
import { createPortal } from "react-dom";

import { Dropdown, type DropdownOption } from "./Dropdown";

/**
 * Canonical popup size used across the entire codebase.
 * Width: max-w-2xl (672px) — comfortable for forms, confirmations, and pickers.
 * Height: capped at 85vh so the popup never touches the viewport edges.
 *
 * The `size` prop is intentionally ignored — every popup in the app uses
 * the same dimensions for visual consistency. The prop is kept on the
 * interface only for backwards compatibility with existing call sites.
 */
const POPUP_WIDTH_CLASS = "max-w-2xl";
const POPUP_WIDE_WIDTH_CLASS = "max-w-5xl";
/** A step above `wide` for content that needs a little more room without
 *  going to the full data-table width. */
const POPUP_WIDER_WIDTH_CLASS = "max-w-6xl";
/** Wide data tables only. An 8-to-11 column grid runs out of room at `wide`
 *  and starts breaking values mid-token. A max, so it still shrinks on
 *  smaller viewports. */
const POPUP_EXTRA_WIDE_WIDTH_CLASS = "max-w-[88rem]";
const POPUP_NARROW_WIDTH_CLASS = "max-w-sm";

/**
 * Canonical popup widths. Named sizes rather than per-call-site CSS, so the
 * app has a handful of consistent dialog shapes instead of 159 bespoke ones.
 * Mirrors the `variant` union `ModalShell` already uses.
 */
export type ModalVariant =
  | "default"
  | "wide"
  | "wider"
  | "extraWide"
  | "narrow";

const VARIANT_WIDTH_CLASS: Record<ModalVariant, string> = {
  default: POPUP_WIDTH_CLASS,
  wide: POPUP_WIDE_WIDTH_CLASS,
  wider: POPUP_WIDER_WIDTH_CLASS,
  extraWide: POPUP_EXTRA_WIDE_WIDTH_CLASS,
  narrow: POPUP_NARROW_WIDTH_CLASS,
};
const POPUP_HEIGHT_CLASS = "max-h-[min(720px,calc(100dvh-2rem))]";

interface ModalProps {
  open: boolean;
  onClose: () => void;
  title: string;
  description?: string;
  children: React.ReactNode;
  /** Deprecated and ignored — kept only so old call sites still compile. */
  size?: "sm" | "md" | "lg" | "xl" | "2xl";
  /**
   * Canonical popup width. Prefer this over the `wide` / `narrow` booleans,
   * which remain only as back-compat aliases. When set, it wins over both.
   */
  variant?: ModalVariant;
  /** @deprecated Use `variant="wide"`. */
  wide?: boolean;
  /** @deprecated Use `variant="narrow"`. */
  narrow?: boolean;
  footer?: React.ReactNode;
  closable?: boolean;
  /** When provided, renders a back arrow in the header on the left of the title. */
  onBack?: () => void;
  /** Controls placed at the right end of the header, before the close button. */
  headerActions?: React.ReactNode;
  icon?: React.ReactNode;
  /** Raise nested modal content above an already-open dialog when needed. */
  zIndexClass?: string;
}

export function Modal({
  open,
  onClose,
  title,
  description,
  children,
  footer,
  closable = true,
  onBack,
  headerActions,
  icon,
  variant,
  wide = false,
  narrow = false,
  zIndexClass = "z-50",
}: ModalProps) {
  // `variant` wins; the booleans are the legacy path. Resolving here means the
  // 159 existing call sites keep their current width with no edit.
  const resolvedVariant: ModalVariant =
    variant ?? (wide ? "wide" : narrow ? "narrow" : "default");
  const overlayRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === "Escape" && closable) onClose();
    };
    document.addEventListener("keydown", handleKey);
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", handleKey);
      document.body.style.overflow = "";
    };
  }, [open, onClose, closable]);

  if (!open) return null;

  return createPortal(
    // pointer-events-auto: counters a drawer's body scroll-lock (pointer-events: none) that would otherwise block this portal.
    <div data-modal-root className={`pointer-events-auto fixed inset-0 right-[var(--companion-rail,0px)] ${zIndexClass} overflow-y-auto`}>
      <div className="fixed inset-0 right-[var(--companion-rail,0px)] bg-background/80 backdrop-blur-sm animate-in fade-in duration-200" />

      <div
        ref={overlayRef}
        className="relative flex min-h-full w-full items-center justify-center p-4"
      >
        <div
          className={`relative w-full ${VARIANT_WIDTH_CLASS[resolvedVariant]} rounded-xl border border-border bg-card shadow-xl flex flex-col animate-in fade-in zoom-in-95 slide-in-from-bottom-2 duration-200 ease-out`}
        >
        <div
          className={`flex justify-between gap-4 p-5 border-b border-border flex-shrink-0 rounded-t-xl ${
            description ? "items-start" : "items-center"
          }`}
        >
          <div
            className={`flex gap-2.5 min-w-0 ${
              description ? "items-start" : "items-center"
            }`}
          >
            {onBack && (
              <button
                onClick={onBack}
                className="flex-shrink-0 p-1.5 -ml-1.5 rounded-md d hover:text-foreground hover:bg-accent transition-colors"
                aria-label="Back"
              >
                <ArrowLeft className="size-4" />
              </button>
            )}
            {icon}
            <div className="min-w-0">
              <h2 className="text-lg font-semibold text-foreground leading-tight text-start">
                {title}
              </h2>
              {description && (
                <p className="text-sm d mt-2 text-start">{description}</p>
              )}
            </div>
          </div>
          <div className="flex min-w-0 items-center gap-3">
            {headerActions}
            {closable && (
              <button
                onClick={onClose}
                className="flex-shrink-0 p-1.5 -mr-1.5 rounded-md d hover:text-foreground hover:bg-accent transition-colors"
                aria-label="Close"
              >
                <X className="size-4" />
              </button>
            )}
          </div>
        </div>

        <div className="p-5 space-y-4">{children}</div>

        {footer && (
          <div className="flex items-center justify-end gap-3 p-5 border-t border-border flex-shrink-0 bg-card rounded-b-xl">
            {footer}
          </div>
        )}
        </div>
      </div>
    </div>,
    document.body,
  );
}

/**
 * `ModalShell` is the same outer wrapper as `Modal` (backdrop + canonical
 * size + frame), but renders no header/footer chrome — you supply your
 * own internal layout via `children`. Use this when you need bespoke
 * header controls (back button, status chips, multiple action buttons)
 * but still want the standard popup size and backdrop.
 *
 * For 95% of popups, prefer `Modal` instead.
 */
export function ModalShell({
  open,
  onClose,
  children,
  closable = true,
  zIndexClass = "z-50",
  variant = "default",
}: {
  open: boolean;
  onClose: () => void;
  children: React.ReactNode;
  closable?: boolean;
  zIndexClass?: string;
  variant?: "default" | "preview" | "wide";
}) {
  const overlayRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === "Escape" && closable) onClose();
    };
    document.addEventListener("keydown", handleKey);
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", handleKey);
      document.body.style.overflow = "";
    };
  }, [open, onClose, closable]);

  if (!open) return null;

 
  const panelSizeClass =
    variant === "preview"
      ? "max-w-6xl h-[860px] max-h-[min(860px,calc(100dvh-2rem))]"
      : variant === "wide"
        ? "max-w-5xl max-h-[min(760px,calc(100dvh-2rem))]"
        : `${POPUP_WIDTH_CLASS} ${POPUP_HEIGHT_CLASS}`;

  return createPortal(
    <div className={`fixed inset-0 right-[var(--companion-rail,0px)] ${zIndexClass} overflow-hidden`}>
      <div className="fixed inset-0 right-[var(--companion-rail,0px)] bg-background/80 backdrop-blur-sm" />
      <div
        ref={overlayRef}
        className="relative flex min-h-full w-full items-center justify-center p-4"
      >
        <div
          className={`relative w-full ${panelSizeClass} rounded-xl border border-border bg-card shadow-xl flex flex-col overflow-hidden`}
        >
          {children}
        </div>
      </div>
    </div>,
    document.body,
  );
}

/* ── Shared form primitives used across modals ── */

const FormFieldContext = createContext<{ error?: boolean }>({});

export function FormField({
  label,
  required,
  children,
  hint,
  error,
}: {
  label: React.ReactNode;
  required?: boolean;
  children: React.ReactNode;
  hint?: string;
  error?: string;
}) {
  return (
    <FormFieldContext.Provider value={{ error: Boolean(error) }}>
      <div className="space-y-1.5 flex flex-col">
        <label className="text-sm font-medium text-foreground flex items-center w-full">
          <div className="flex items-center">
            {label}
            {required && <span className="text-red-500 ml-1">*</span>}
          </div>
        </label>
        {children}
        {error && <p className="text-xs text-red-500">{error}</p>}
        {hint && !error && <p className="text-xs d">{hint}</p>}
      </div>
    </FormFieldContext.Provider>
  );
}

export function FormInput({
  placeholder,
  defaultValue,
  value,
  onChange,
  type = "text",
  disabled,
  autoComplete,
  name,
  maxLength,
  min,
  max,
  className,
  error,
}: {
  placeholder?: string;
  defaultValue?: string;
  value?: string;
  onChange?: (v: string) => void;
  type?: string;
  disabled?: boolean;
  autoComplete?: string;
  name?: string;
  maxLength?: number;
  min?: number;
  max?: number;
  className?: string;
  error?: boolean;
}) {
  const fieldContext = useContext(FormFieldContext);
  const isInvalid = error ?? fieldContext?.error;
  const isDate = type === "date";
  return (
    <input
      type={type}
      name={name}
      autoComplete={autoComplete}
      lang={isDate ? "en-GB" : undefined}
      placeholder={placeholder}
      defaultValue={defaultValue}
      value={value}
      onChange={onChange ? (e) => onChange(e.target.value) : undefined}
      disabled={disabled}
      maxLength={maxLength}
      min={min}
      max={max}
      onClick={
        isDate
          ? (e) => {
              const el = e.currentTarget as HTMLInputElement & {
                showPicker?: () => void;
              };
              el.showPicker?.();
            }
          : undefined
      }
      style={isDate ? { colorScheme: "light dark" } : undefined}
      className={`w-full text-sm bg-[#f5f7fb] dark:bg-white/[0.04] rounded-lg px-3 py-2 outline-none border ${
        isInvalid
          ? "!border-red-500 dark:!border-red-500 hover:!border-red-500 dark:hover:!border-red-500 focus:!border-red-500 dark:focus:!border-red-500 focus:ring-2 focus:ring-red-500/10"
          : "border-[#e2e8f0] dark:border-white/[0.07] focus:border-indigo-500 dark:focus:border-indigo-500 focus:ring-2 focus:ring-indigo-500/10 dark:focus:ring-indigo-500/10"
      } transition-all placeholder:text-[#64748b] dark:placeholder:text-white/25 text-[#0f172a] dark:text-white disabled:opacity-50 ${isDate ? "cursor-pointer [&::-webkit-datetime-edit-fields-wrapper]:bg-transparent [&::-webkit-datetime-edit]:bg-transparent [&::selection]:bg-transparent" : ""} ${className ?? ""}`}
    />
  );
}

export function FormSelect({
  options,
  value,
  onChange,
  placeholder,
  searchable,
  loading,
  disabled,
  className,
  error,
  clearable,
}: {
  /** A bare string is its own value and label. The object form is the full
   *  `DropdownOption`, so an option can also carry a dot or a badge icon. */
  options: (string | DropdownOption)[];
  value?: string;
  onChange?: (v: string) => void;
  placeholder?: string;
  /** Shows a search box in the option list — for long lists like countries. */
  searchable?: boolean;
  /** Options are still loading — spins the trigger icon and shows a
   *  loading row instead of an empty option list. */
  loading?: boolean;
  disabled?: boolean;
  className?: string;
  error?: boolean;
  clearable?: boolean;
}) {
  const fieldContext = useContext(FormFieldContext);
  const isInvalid = error ?? fieldContext?.error;
  // Memoized so a long list isn't rebuilt every time the surrounding form
  // re-renders — a modal typically re-renders on each keystroke in any field.
  const normalized = useMemo(
    () => options.map((o) => (typeof o === "string" ? { value: o, label: o } : o)),
    [options],
  );
  return (
    <Dropdown
      value={value ?? ""}
      onChange={onChange ?? (() => {})}
      options={normalized}
      placeholder={placeholder}
      variant="form"
      searchable={searchable}
      loading={loading}
      disabled={disabled}
      error={isInvalid}
      className={className}
      clearable={clearable}
    />
  );
}

export function FormTextarea({
  placeholder,
  value,
  onChange,
  rows = 3,
  maxRows = 8,
  className,
  error,
}: {
  placeholder?: string;
  value?: string;
  onChange?: (v: string) => void;
  rows?: number;
  maxRows?: number;
  className?: string;
  error?: boolean;
}) {
  const fieldContext = useContext(FormFieldContext);
  const isInvalid = error ?? fieldContext?.error;
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    const el = textareaRef.current;
    if (!el) return;
    el.style.height = "auto";
    const lineHeight = parseFloat(getComputedStyle(el).lineHeight || "20") || 20;
    const maxHeight = lineHeight * maxRows;
    el.style.height = `${Math.min(el.scrollHeight, maxHeight)}px`;
  }, [value, maxRows]);

  return (
    <textarea
      ref={textareaRef}
      placeholder={placeholder}
      value={value}
      onChange={onChange ? (e) => onChange(e.target.value) : undefined}
      rows={rows}
      className={`w-full text-sm bg-[#f5f7fb] dark:bg-white/[0.04] rounded-lg px-3 py-2 outline-none border ${
        isInvalid
          ? "!border-red-500 dark:!border-red-500 hover:!border-red-500 dark:hover:!border-red-500 focus:!border-red-500 dark:focus:!border-red-500"
          : "border-[#e2e8f0] dark:border-white/[0.07] focus:border-indigo-500 dark:focus:border-indigo-500"
      } transition-all placeholder:text-[#64748b] dark:placeholder:text-white/25 resize-none leading-relaxed text-[#0f172a] dark:text-white overflow-y-auto ${className ?? ""}`}
    />
  );
}

/**
 * Native date picker styled to show DD/MM/YYYY.
 * `value` and `onChange` use YYYY-MM-DD (ISO) format internally.
 */
export function FormDatePicker({
  value,
  onChange,
  placeholder = "DD/MM/YYYY",
  min,
}: {
  value?: string;
  onChange?: (v: string) => void;
  placeholder?: string;
  min?: string;
}) {
  const display = value
    ? new Date(value + "T00:00:00").toLocaleDateString("en-GB")
    : placeholder;

  return (
    <div
      className="relative cursor-pointer"
      onClick={(e) => {
        const input = (e.currentTarget as HTMLElement).querySelector("input");
        if (input) {
          try {
            input.showPicker();
          } catch {
            input.focus();
          }
        }
      }}
    >
      <input
        type="date"
        value={value ?? ""}
        onChange={onChange ? (e) => onChange(e.target.value) : undefined}
        min={min}
        className="w-full px-3 py-2 text-sm bg-[#f5f7fb] dark:bg-white/[0.04] border border-[#e2e8f0] dark:border-white/[0.07] rounded-lg outline-none focus:border-indigo-500 dark:focus:border-indigo-500 transition-all text-transparent dark:text-transparent cursor-pointer [&::-webkit-datetime-edit]:text-transparent [&::-webkit-datetime-edit-fields-wrapper]:text-transparent [&::-webkit-calendar-picker-indicator]:opacity-0 [&::-webkit-calendar-picker-indicator]:absolute [&::-webkit-calendar-picker-indicator]:inset-0 [&::-webkit-calendar-picker-indicator]:w-full [&::-webkit-calendar-picker-indicator]:h-full [&::-webkit-calendar-picker-indicator]:cursor-pointer"
      />
      <span className="absolute left-3 top-1/2 -translate-y-1/2 text-sm pointer-events-none text-[#0f172a] dark:text-white">
        {display}
      </span>
      <CalendarDays className="absolute right-3 top-1/2 -translate-y-1/2 size-4 d pointer-events-none" />
    </div>
  );
}

/** Accent colours for the primary footer button — lets a confirm dialog match
 *  the action it performs (green approve, red destructive, …). */
const SUBMIT_TONE_CLASS: Record<
  "indigo" | "emerald" | "blue" | "red",
  string
> = {
  indigo:
    "bg-indigo-600 dark:bg-indigo-600 hover:bg-indigo-700 dark:hover:bg-indigo-700 shadow-indigo-500/20 dark:shadow-indigo-500/20",
  emerald:
    "bg-emerald-600 dark:bg-emerald-600 hover:bg-emerald-700 dark:hover:bg-emerald-700 shadow-emerald-500/20 dark:shadow-emerald-500/20",
  blue: "bg-blue-600 dark:bg-blue-600 hover:bg-blue-700 dark:hover:bg-blue-700 shadow-blue-500/20 dark:shadow-blue-500/20",
  red: "bg-red-600 dark:bg-red-600 hover:bg-red-700 dark:hover:bg-red-700 shadow-red-500/20 dark:shadow-red-500/20",
};

export function ModalFooterButtons({
  onCancel,
  onSubmit,
  submitLabel = "Save",
  submitDisabled,
  isSubmitting,
  cancelText = "Cancel",
  submitTone = "indigo",
}: {
  onCancel: () => void;
  /** Omit for a dismiss-only footer — the primary button isn't rendered. */
  onSubmit?: () => void;
  submitLabel?: React.ReactNode;
  submitDisabled?: boolean;
  isSubmitting?: boolean;
  /** Label of the secondary button, e.g. "Close" on a read-only modal. */
  cancelText?: string;
  /** Accent colour of the primary button. Defaults to the app's indigo. */
  submitTone?: "indigo" | "emerald" | "blue" | "red";
}) {
  return (
    <div className="flex w-full items-center justify-end gap-3">
      <button
        onClick={onCancel}
        className="px-4 py-2 rounded-lg border border-[#e2e8f0] dark:border-white/[0.07] text-sm font-medium text-[#475569] dark:text-white/60 hover:bg-[#f1f5f9] dark:hover:bg-white/[0.04] transition-colors"
      >
        {cancelText}
      </button>
      {onSubmit && (
        <button
          onClick={onSubmit}
          disabled={submitDisabled || isSubmitting}
          className={`px-4 py-2 rounded-lg text-white text-sm font-bold transition-colors disabled:opacity-40 shadow-md flex items-center justify-center gap-2 ${SUBMIT_TONE_CLASS[submitTone]}`}
        >
          {isSubmitting && <Loader2 className="size-4 animate-spin" />}
          {submitLabel}
        </button>
      )}
    </div>
  );
}


